import { useCallback, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import useCallbackUnignored from 'hooks/useCallbackUnignored.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import getCroppedImg from 'utils/image/getCroppedImg.util';

import CN from './ImageCropper.module.scss';

const initialCropperState = { crop: { x: 0, y: 0 }, zoom: 1, rotation: 0 };

const reducerHandlers = Object.freeze({
  SET_CROP: (state, action) => ({ ...state, crop: action.payload }),
  SET_ZOOM: (state, action) => ({ ...state, zoom: action.payload }),
  SET_ROTATION: (state, action) => ({ ...state, rotation: action.payload }),
  RESET: () => initialCropperState,
  SET_ALL: (state, action) => ({ ...state, ...action.payload }),
});

/**
 * ImageCropper - Reusable component for cropping and rotating images
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {string} imageSrc - The source image URL (existing/saved image)
 * @param {function} onClose - Callback when modal closes
 * @param {function} onSave - Callback when image is saved, receives { croppedFile, croppedImageUrl }
 * @param {function} onRemove - Callback to remove the current image
 * @param {React.Node|string} modalTitle - Optional title for the modal
 * @param {boolean} hasControls - Whether to show change/remove buttons
 * @param {string[]} allowedMimeTypes - Allowed MIME types for upload
 * @param {number} maxFileSizeBytes - Maximum file size in bytes for upload
 * @param {function} onInvalidUpload - Callback when an invalid file is uploaded
 */
const ImageCropper = ({
  isOpen,
  imageSrc,
  onClose = alwaysNoop,
  onSave = alwaysNoop,
  onRemove = alwaysNoop,
  modalTitle = null,
  hasControls = true,
  allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  maxFileSizeBytes = 5 * 1024 * 1024, // 5 MB
  onInvalidUpload = alwaysNoop,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const fileRef = useRef(null);

  const [cropperState, cropperStateDispatch] = useReducer((state, action) => {
    const handler = reducerHandlers[action.type];
    return handler ? handler(state, action) : state;
  }, initialCropperState);

  const { crop, zoom, rotation } = cropperState;

  const setCrop = useCallback((c) => cropperStateDispatch({ type: 'SET_CROP', payload: c }), [cropperStateDispatch]);
  const setZoom = useCallback((z) => cropperStateDispatch({ type: 'SET_ZOOM', payload: z }), [cropperStateDispatch]);
  const setRotation = useCallback((r) => cropperStateDispatch({ type: 'SET_ROTATION', payload: r }), [cropperStateDispatch]);
  const resetCropper = useCallback(() => cropperStateDispatch({ type: 'RESET' }), [cropperStateDispatch]);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [invalidUploadMsg, setInvalidUploadMsg] = useState(null);

  const { val: isMarkedForRemoval, inv: isNotMarkedForRemoval, yay: markForRemoval, nay: unmarkForRemoval } = useTip(false);

  const onUploadButtonClick = useCallback(() => {
    fileRef?.current.click();
  }, [fileRef]);

  const closeModal = useCallback(() => {
    // Clean up the preview URL when closing modal
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    resetCropper();
    setInvalidUploadMsg(null);
    setCroppedAreaPixels(null);
    unmarkForRemoval();
    // Clear the file input so the same file can be selected again
    if (fileRef?.current) fileRef.current.value = '';

    call('ImageCropper.onClose', onClose);
  }, [previewUrl, setPreviewUrl, resetCropper, setCroppedAreaPixels, unmarkForRemoval, onClose, fileRef]);

  const handleCropComplete = useCallback(
    (_, croppedPixels) => {
      void setCroppedAreaPixels(croppedPixels);
    },
    [setCroppedAreaPixels],
  );

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        // Validate mime type
        const mimeType = fromAnyToTrimmed(file.type) || '';
        if (!allowedMimeTypes.includes(mimeType)) {
          const err = {
            reason: 'INVALID_MIME_TYPE',
            message: t('imageCropper.invalidMimeType', {
              type: mimeType || t('imageCropper.unknown'),
            }),
            file,
          };
          setInvalidUploadMsg(err);
          call('ImageCropper.onInvalidUpload', onInvalidUpload, err);
          // clear input so same file can be selected again later
          if (fileRef?.current) fileRef.current.value = '';
          return;
        }

        // Validate file size
        if (typeof file.size === 'number' && file.size > maxFileSizeBytes) {
          const sizeMb = fromAnyToNumber((file.size / 1024 / 1024).toFixed(2));
          const maxSizeMb = fromAnyToNumber((maxFileSizeBytes / 1024 / 1024).toFixed(2));
          const err = {
            reason: 'FILE_TOO_LARGE',
            message: t('imageCropper.fileTooLarge', {
              sizeMb,
              maxSizeMb,
            }),
            file,
          };
          setInvalidUploadMsg(err);
          call('ImageCropper.onInvalidUpload', onInvalidUpload, err);
          if (fileRef?.current) fileRef.current.value = '';
          return;
        }

        // Create blob URL for cropper modal preview (not for display)
        const newPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl(newPreviewUrl);
        setInvalidUploadMsg(null);
        resetCropper();
        setCroppedAreaPixels(null);
        // Clear the removal flag so the new image is displayed
        unmarkForRemoval();
      }
    },
    [resetCropper, setCroppedAreaPixels, setPreviewUrl, unmarkForRemoval, allowedMimeTypes, maxFileSizeBytes, onInvalidUpload, fileRef, t],
  );

  const handleSave = useCallbackUnignored(async () => {
    if (isMarkedForRemoval) {
      call('ImageCropper.onRemoveImage', onRemove);
    } else if ((previewUrl || imageSrc) && croppedAreaPixels) {
      // Handle saving cropped image (either from new file or existing image)
      const imageToUse = previewUrl || imageSrc;
      const croppedFile = await getCroppedImg(imageToUse, croppedAreaPixels, rotation);
      const croppedImageUrl = URL.createObjectURL(croppedFile);

      if (fileRef?.current) {
        fileRef.current.value = '';
      }
      call('ImageCropper.onSave', onSave, { croppedFile, croppedImageUrl });
    }

    closeModal();
  }, [previewUrl, imageSrc, croppedAreaPixels, rotation, isMarkedForRemoval, onSave, onRemove, closeModal, fileRef]);

  const image = fromAnyToTrimmed(previewUrl) || fromAnyToTrimmed(imageSrc) || void 1;
  const hasImage = fromAnyToBool(isNotMarkedForRemoval && image);
  // Only enable controls (sliders) if there's a newly uploaded image (previewUrl exists)
  const isControlsDisabled = !hasImage || !previewUrl;
  const isSaveDisabled = hasImage && !previewUrl;

  if (!isOpen) return null;

  return (
    <div className={cn(CN.modalBackdrop, { [CN.dark]: isDarkMode })}>
      <div className={cn(CN.modalDialog, { [CN.dark]: isDarkMode })}>
        <div className={CN.modalHeader}>
          {modalTitle && <h3>{modalTitle}</h3>}
          <Button variant="empty" onClick={closeModal}>
            <Icon variant="close" />
          </Button>
        </div>

        <input data-bem="ImageCropper__image-file-upload" ref={fileRef} type="file" accept={allowedMimeTypes.join(',')} onChange={handleFileChange} style={{ display: 'none' }} />

        <div className={CN.cropperContainer}>
          <div className={CN.cropArea}>
            {hasImage ? (
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={handleCropComplete}
              />
            ) : (
              <div className={CN.noImagePreview}>
                <Icon variant="user" width="l3" selected variantInvertDark />
              </div>
            )}
          </div>

          {!hasImage && (
            <Button variant="primary" onClick={onUploadButtonClick}>
              {t('imageCropper.uploadImage')}
            </Button>
          )}

          {invalidUploadMsg && (
            <div className={CN.invalidMessage} data-bem="ImageCropper__invalid-upload">
              {invalidUploadMsg.message}
            </div>
          )}

          {fromAnyToBool(hasImage && hasControls) && (
            <div className={CN.imageControlButtons}>
              <Button variant="outline" onClick={onUploadButtonClick}>
                {t('imageCropper.changeImage')}
              </Button>
              <Button variant="outline" onClick={markForRemoval}>
                {t('imageCropper.removeImage')}
              </Button>
            </div>
          )}

          <div className={CN.controlsWrapper}>
            <div className={CN.cropControls} data-is-disabled={String(isControlsDisabled)}>
              <div>
                <span className={CN.cropHint}>{t('imageCropper.adjustHint')}</span>
              </div>

              <div className={CN.controlGroup}>
                <label className={CN.controlLabel}>
                  <Icon variant="minusV2" width="md" selected />
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(ev) => setZoom(fromAnyToNumber(fromEventToValue(ev)))}
                    className={CN.zoomRange}
                    disabled={isControlsDisabled}
                  />
                  <Icon variant="plusV2" width="md" selected />
                </label>
              </div>

              <div className={CN.controlGroup}>
                <label className={CN.controlLabel}>
                  <Icon variant="arrowRotate" width="md" selected />
                  <input
                    type="range"
                    value={rotation}
                    min={0}
                    max={360}
                    step={1}
                    onChange={(ev) => setRotation(fromAnyToNumber(fromEventToValue(ev)))}
                    className={CN.rotationRange}
                    disabled={isControlsDisabled}
                  />
                  <Icon variant="arrowRotate" width="md" selected />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className={CN.modalFooter}>
          <Button variant="tertiary" onClick={closeModal}>
            {t('imageCropper.cancel')}
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={isSaveDisabled}>
            {t('imageCropper.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImageCropperHoc = (props) => createPortal(<ImageCropper {...props} />, document.body);

export default ImageCropperHoc;
